import { inject } from 'mobx-react';
import React from 'react';
import { BackendSourceOptions } from '@udemy/browse-event-tracking';
import { trackGenericCourseClick, TrackImpression } from '@udemy/event-tracking';
import { useMatchMedia } from '@udemy/hooks';
import { useI18n } from '@udemy/i18n';
import { withFeatures } from '@udemy/react-experimentation-provider';
import { Ribbon } from '@udemy/react-messaging-components';
import { CardTitle, CardMediaImage, CourseProductCard } from '@udemy/react-product-card-components';
import { PlayOverlay } from '@udemy/react-structure-components';
import { useUDData } from '@udemy/ud-data';
import { COURSE_BADGE_PREMIUM } from '../course-badges/constants';
import { BrowseCourseBadgeHandler } from '../course-badges/course-badges.react-component';
import { withBrowseCourseBadgesContext } from '../course-badges/with-browse-course-badges-context';
import { CourseCardSeoInfo } from '../course-card-seo-info/course-card-seo-info.react-component';
import { CourseCardDescription } from '../course-product-card/course-card-description.react-component';
import { CourseCardInstructors } from '../course-product-card/course-card-instructors.react-component';
import { courseCardMediaProps, buildWideSrcSetAttributes } from '../course-product-card/course-card-media';
import { priceTextPropsWithTracking } from '../course-product-card/course-card-price';
import { CourseCardTitleWithAnchor } from '../course-product-card/course-card-title-with-anchor.react-component';
import { ribbonsToRibbonTags, CourseCardCourseTag, CourseCardLastUpdatedTag, CourseCardRatingTag, CourseCardRatingCountTag, CourseCardContentInfoTag, CourseCardInstructorLevelTag, CourseCardPublishedLecturesTag } from '../course-product-card/tags.react-component';
import { PrefetchingWrapper } from '../prefetching-wrapper/prefetching-wrapper.react-component';
import { AsyncCourseStaticPriceText, StaticPriceText } from '../price-text';
import styles from "./browse-course-product-card.module.css";
import { CourseCardDebug } from './course-card-debug.react-component';
import { trackCourseClickEvent } from './course-card-tracking';
const OPEN_IN_NEW_TAB_FEATURE = 'course_cards_open_in_new_tab';
const PP_EXPLICIT_ENROLL_FEATURE = 'personal_plan_course_enrollment';
const PersonalPlanRibbon = () => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Ribbon, {
    className: styles['personal-plan-ribbon']
  }, gettext('Personal Plan'));
};
PersonalPlanRibbon.displayName = 'PersonalPlanRibbon';

/**
 * Component to render status text for Grow with Google courses.
 */
const GrowWithGoogleStatusText = () => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement("div", {
    className: styles['grow-with-google-status']
  }, /*#__PURE__*/React.createElement("span", {
    className: "ud-text-bold"
  }, gettext('See course for details')));
};
GrowWithGoogleStatusText.displayName = 'GrowWithGoogleStatusText';
export const InternalBrowseCourseProductCard = props => {
  var _course$contentCollec, _experimentationResul;
  const {
    course,
    isUserEnrolled,
    trackingContext,
    secondaryContent,
    showListPriceOnly,
    showRating = true,
    showRatingCount = true,
    showCourseTag = true,
    showLastUpdatedTag = false,
    showRibbons = true,
    showDetails = true,
    showPrice = true,
    renderSeoInfo = true,
    experimentationResult,
    showDescription = true,
    ribbonHighlighted = true,
    disablePremiumHighlightedRibbon = false,
    showRibbonsLast = false,
    browseBadgeHandlerOptions,
    trackingClickCallbackFunc,
    isAsyncStaticPrice = false,
    showPersonalPlanBadge = false,
    trackMiddleClick = false,
    trackImpressionProps = {},
    cardTitleProps = {},
    showRibbonsOverride = false,
    ...restProps
  } = props;
  const {
    Config: udConfig
  } = useUDData();
  const isUB = !!udConfig.brand.has_organization;
  const isBuyable = !(course.is_in_user_subscription || isUserEnrolled);
  const isRibbonsShown = showRibbonsOverride || isBuyable && showRibbons;

  // Check if course is included in Grow with Google subscription
  const isGrowWithGoogleCourse = !!((_course$contentCollec = course.contentCollectionInfo) !== null && _course$contentCollec !== void 0 && _course$contentCollec.isInGooglePartnerCollection);
  const {
    getBadgeComponents,
    getBadgeFamilies
  } = new BrowseCourseBadgeHandler(course, browseBadgeHandlerOptions);
  const personalPlanExplicitEnrollAssignment = experimentationResult === null || experimentationResult === void 0 || (_experimentationResul = experimentationResult.featureVariantAssignments) === null || _experimentationResul === void 0 ? void 0 : _experimentationResul.get(PP_EXPLICIT_ENROLL_FEATURE);
  const personalPlanExplicitEnroll = personalPlanExplicitEnrollAssignment === null || personalPlanExplicitEnrollAssignment === void 0 ? void 0 : personalPlanExplicitEnrollAssignment.configuration.enablePersonalPlanEnrollments;
  const courseURL = course.is_in_user_subscription && (!personalPlanExplicitEnroll || isUserEnrolled) ? course.learn_url : course.url;
  const isPersonalPlanRibbonShown = course.is_in_personal_plan_collection && showPersonalPlanBadge;
  const priceTextProps = priceTextPropsWithTracking({
    course,
    priceTextProps: {
      showListPriceOnly
    }
  });
  const {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    listPrice,
    discountPrice,
    listPriceString,
    discountPriceString,
    /* eslint-enable @typescript-eslint/no-unused-vars */
    ...restPriceTextProps
  } = priceTextProps;
  const EnrolledMediaContent = ({
    sizes,
    srcSet,
    src,
    layout
  }) => /*#__PURE__*/React.createElement("div", {
    className: styles.image
  }, /*#__PURE__*/React.createElement(CardMediaImage, {
    layout: layout,
    src: src,
    srcSet: srcSet,
    sizes: sizes
  }), /*#__PURE__*/React.createElement("div", {
    className: styles['opacity-overlay-dark']
  }), /*#__PURE__*/React.createElement(PlayOverlay, {
    className: styles['enrolled-play-overlay']
  }));
  const trackImpression = () => {
    if (!(trackingContext !== null && trackingContext !== void 0 && trackingContext.trackImpressionFunc)) return;
    const badgeHandler = new BrowseCourseBadgeHandler(course, browseBadgeHandlerOptions);
    const {
      trackImpressionFunc,
      relatedSourceId,
      relatedSourceType,
      ...restTrackingContext
    } = trackingContext;
    trackImpressionFunc({
      item: {
        ...course,
        visibleBadgeFamilies: badgeHandler.getBadgeFamilies()
      }
    }, restTrackingContext, {
      relatedSourceId,
      relatedSourceType
    });
  };
  const trackClick = event => {
    const {
      index,
      uiRegion
    } = trackingContext;
    const badgeHandler = new BrowseCourseBadgeHandler(course, browseBadgeHandlerOptions);
    const courseTrackingId = course.frontendTrackingId || course.tracking_id;
    trackGenericCourseClick({
      courseId: course.id,
      componentName: 'courseCard',
      courseTrackingId: courseTrackingId
    });
    trackCourseClickEvent({
      ...course,
      visibleBadgeFamilies: badgeHandler.getBadgeFamilies()
    }, index, BackendSourceOptions.DISCOVERY, uiRegion);
    trackingClickCallbackFunc === null || trackingClickCallbackFunc === void 0 || trackingClickCallbackFunc(event);
  };
  const handleMouseMiddleClick = event => {
    // Means mouse middle click
    if (event.button === 1) {
      trackClick(event);
    }
  };
  const defaultTitleWithAnchorProps = {
    href: courseURL,
    onClick: trackClick,
    onContextMenu: trackClick,
    ...(trackMiddleClick && {
      onMouseUp: handleMouseMiddleClick
    }),
    ...cardTitleProps
  };

  // it should open a new tab only on desktop
  const isMobile = useMatchMedia('mobile-max');
  const openInNewTab = !isMobile;
  const titleWithAnchorProps = openInNewTab ? {
    target: '_blank',
    rel: 'noopener',
    ...defaultTitleWithAnchorProps
  } : defaultTitleWithAnchorProps;
  const additionalTags = [...(showCourseTag ? [/*#__PURE__*/React.createElement(CourseCardCourseTag, {
    key: 'static_course_tag'
  })] : []), ...(showRating ? [/*#__PURE__*/React.createElement(CourseCardRatingTag, {
    key: "rating-tag",
    course: course
  }), showRatingCount && /*#__PURE__*/React.createElement(CourseCardRatingCountTag, {
    key: "rating-count-tag",
    course: course
  })] : []), ...(showDetails ? [/*#__PURE__*/React.createElement(CourseCardContentInfoTag, {
    key: "content-info-tag",
    course: course
  }), /*#__PURE__*/React.createElement(CourseCardPublishedLecturesTag, {
    key: "published-lectures-tag",
    course: course
  }), /*#__PURE__*/React.createElement(CourseCardInstructorLevelTag, {
    key: "instructor-level-tag",
    course: course
  })] : []), ...(isPersonalPlanRibbonShown ? [/*#__PURE__*/React.createElement(PersonalPlanRibbon, {
    key: "personal-plan-ribbon"
  })] : []), ...(showLastUpdatedTag ? [/*#__PURE__*/React.createElement(CourseCardLastUpdatedTag, {
    key: 'static_last_updated_tag',
    course: course
  })] : [])];
  const badgeFamilies = getBadgeFamilies();
  const ribbonTags = !showRibbonsOverride && isUB ? [] : ribbonsToRibbonTags([...getBadgeComponents()]);
  const [highlightedRibbonTag, ...remainingRibbonTags] = ribbonTags;
  const disabledHighlightedRibbon = disablePremiumHighlightedRibbon && badgeFamilies.length === 1 && badgeFamilies[0] === COURSE_BADGE_PREMIUM;
  const isHighlightedRibbonEnabled = ribbonHighlighted && !disabledHighlightedRibbon;
  const combinedTags = isRibbonsShown ? showRibbonsLast ? [...additionalTags, ...remainingRibbonTags] : [...remainingRibbonTags, ...additionalTags] : [...additionalTags];
  const finalTagsOrder = isRibbonsShown && !isHighlightedRibbonEnabled && highlightedRibbonTag ? showRibbonsLast ? [...combinedTags, highlightedRibbonTag] : [highlightedRibbonTag, ...combinedTags] : combinedTags;
  const tags = finalTagsOrder.length > 0 ? finalTagsOrder : undefined;
  const getMediaProps = () => {
    if (isUserEnrolled) {
      return {
        mediaSquare: /*#__PURE__*/React.createElement(EnrolledMediaContent, Object.assign({
          layout: "square",
          src: course.image_50x50
        }, buildWideSrcSetAttributes({
          course
        }))),
        mediaWide: /*#__PURE__*/React.createElement(EnrolledMediaContent, Object.assign({
          layout: "wide",
          src: course.image_240x135
        }, buildWideSrcSetAttributes({
          course
        })))
      };
    }
    return courseCardMediaProps({
      course
    });
  };
  const renderTitle = () => {
    let title = /*#__PURE__*/React.createElement(CardTitle, Object.assign({
      headingLevel: "h3"
    }, cardTitleProps), course.title);
    if (courseURL) {
      title = /*#__PURE__*/React.createElement(CourseCardTitleWithAnchor, Object.assign({
        course: course,
        headingLevel: "h3"
      }, titleWithAnchorProps));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, title, renderSeoInfo && /*#__PURE__*/React.createElement(CourseCardSeoInfo, {
      course: course
    }));
  };
  const renderPriceText = () => {
    // Hide price for Grow with Google courses
    if (isGrowWithGoogleCourse) {
      return /*#__PURE__*/React.createElement(GrowWithGoogleStatusText, null);
    }
    if (isUB || !isBuyable || !showPrice) return undefined;
    if (isAsyncStaticPrice) return /*#__PURE__*/React.createElement(AsyncCourseStaticPriceText, Object.assign({
      courses: [course]
    }, restPriceTextProps));
    return /*#__PURE__*/React.createElement(StaticPriceText, priceTextProps);
  };
  const renderSecondaryContent = () => {
    return course.input_features ? /*#__PURE__*/React.createElement(React.Fragment, null, secondaryContent, /*#__PURE__*/React.createElement(CourseCardDebug, {
      course: course
    })) : secondaryContent;
  };
  const courseCard = /*#__PURE__*/React.createElement(PrefetchingWrapper, {
    url: courseURL
  }, /*#__PURE__*/React.createElement(CourseProductCard, Object.assign({}, restProps, {
    tags: tags,
    title: renderTitle(),
    statusLabel: renderPriceText(),
    secondaryContent: renderSecondaryContent(),
    authors: /*#__PURE__*/React.createElement(CourseCardInstructors, {
      course: course
    }),
    description: showDescription ? /*#__PURE__*/React.createElement(CourseCardDescription, {
      course: course
    }) : undefined,
    highlightedTag: isRibbonsShown && isHighlightedRibbonEnabled ? highlightedRibbonTag : undefined
  }, getMediaProps())));
  if (!(trackingContext !== null && trackingContext !== void 0 && trackingContext.trackImpressionFunc)) return courseCard;
  return /*#__PURE__*/React.createElement(TrackImpression, Object.assign({}, trackImpressionProps, {
    trackFunc: trackImpression
  }), courseCard);
};
InternalBrowseCourseProductCard.displayName = 'InternalBrowseCourseProductCard';
export const BrowseCourseProductCard = withBrowseCourseBadgesContext(withFeatures([OPEN_IN_NEW_TAB_FEATURE, PP_EXPLICIT_ENROLL_FEATURE], inject(({
  showPersonalPlanBadge = false,
  trackingContext = {}
}) => ({
  trackingContext,
  showPersonalPlanBadge
}))(InternalBrowseCourseProductCard)));
BrowseCourseProductCard.displayName = 'BrowseCourseProductCard';
//# sourceMappingURL=browse-course-product-card.react-component.js.map